/**
 * ProShip Direct v2 — pincode-level zone upload: template, parse, validate.
 *
 * Descriptive zones ("within state", "metro to metro") are how a contract reads, not how it
 * bills. Carriers settle the argument lane by lane, and a merchant who has the lane list has the
 * only version that matches their invoice — so this sits alongside the descriptions rather than
 * replacing them, and wins where both apply.
 *
 * Pure functions, no React, so the validation is testable and the page stays presentational.
 * Nothing is stored until every row has been checked and the merchant has seen what was
 * rejected — the same contract as the rule and serviceability uploads.
 *
 * DEVIATION: a light CSV read (split on newline, then comma), matching data/ruleCsv.js. Every
 * cell here is a number, so there is nothing a quoted comma could legitimately be hiding; a row
 * with the wrong column count is rejected outright rather than guessed at.
 */

export const ZONE_CSV_COLUMNS = ['pickup_pincode', 'delivery_pincode', 'zone'];

export const ZONE_VALUES = [1, 2, 3, 4, 5];

/**
 * How many rows this build keeps.
 *
 * A real zone map is a lane list that can run to tens of thousands of rows, and it belongs in the
 * backend — there is no endpoint for it yet (see the TODO on the page). localStorage is not the
 * place for that volume, so the file is validated in full and the first slice is kept so the
 * screen has something true to show.
 */
export const ZONE_ROW_CAP = 5000;

export const ZONE_CSV_TEMPLATE = [
  ZONE_CSV_COLUMNS.join(','),
  '110001,110096,1',
  '110001,201301,2',
  '110001,560001,3',
  '110001,452001,4',
  '110001,781001,5',
].join('\n');

const isPincode = (value) => /^[1-9][0-9]{5}$/.test(String(value || '').trim());

/** Accepts 3, "3", "Zone 3" and "zone3" — all of which a merchant's own file will contain. */
const readZone = (value) => {
  const match = String(value || '').trim().match(/^(?:zone\s*)?([1-5])$/i);
  return match ? Number(match[1]) : null;
};

const lane = (row) => `${row.pickupPincode}>${row.deliveryPincode}`;

/**
 * Parses a zone-map CSV.
 *
 * Returns `{ ok, error, rows, errors, summary }`. `error` is set only when the file itself is
 * unusable (wrong header, no data); individual bad rows come back in `errors` with their line
 * number, and the good rows still come back in `rows`, so a merchant can fix twelve lines rather
 * than re-export the file.
 */
export function parseZoneCsv(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { ok: false, error: 'The file has a header but no rows.', rows: [], errors: [], summary: null };
  }

  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  if (header.length !== ZONE_CSV_COLUMNS.length || ZONE_CSV_COLUMNS.some((c, i) => header[i] !== c)) {
    return {
      ok: false,
      error: `Header must be exactly: ${ZONE_CSV_COLUMNS.join(', ')}. Download the template and fill that in.`,
      rows: [],
      errors: [],
      summary: null,
    };
  }

  const rows = [];
  const errors = [];
  // Lane → zone, so the same pair claiming two different zones is caught rather than last-wins.
  const seen = new Map();

  lines.slice(1).forEach((line, i) => {
    const lineNo = i + 2; // 1-indexed, and the header is line 1
    const cells = line.split(',').map((c) => c.trim());

    if (cells.length !== ZONE_CSV_COLUMNS.length) {
      errors.push({ line: lineNo, message: `Expected ${ZONE_CSV_COLUMNS.length} columns, found ${cells.length}` });
      return;
    }

    const [pickup, delivery, zoneCell] = cells;

    if (!isPincode(pickup)) {
      errors.push({ line: lineNo, message: `"${pickup}" is not a six-digit pincode` });
      return;
    }
    if (!isPincode(delivery)) {
      errors.push({ line: lineNo, message: `"${delivery}" is not a six-digit pincode` });
      return;
    }

    const zone = readZone(zoneCell);
    if (!zone) {
      errors.push({ line: lineNo, message: `"${zoneCell}" is not a zone — use 1 to 5` });
      return;
    }

    const row = { pickupPincode: pickup, deliveryPincode: delivery, zone };
    const key = lane(row);
    const prior = seen.get(key);

    if (prior && prior.zone !== zone) {
      errors.push({
        line: lineNo,
        message: `${pickup} → ${delivery} is already Zone ${prior.zone} on line ${prior.line}`,
      });
      return;
    }
    if (prior) return; // exact duplicate: harmless, and dropping it silently is the kind thing

    seen.set(key, { zone, line: lineNo });
    rows.push(row);
  });

  const byZone = ZONE_VALUES.reduce(
    (acc, z) => ({ ...acc, [z]: rows.filter((r) => r.zone === z).length }),
    {}
  );

  return {
    ok: rows.length > 0,
    error: rows.length === 0 ? 'Every row was rejected — nothing to save.' : null,
    rows,
    errors,
    summary: {
      total: rows.length,
      rejected: errors.length,
      byZone,
      pickupPincodes: new Set(rows.map((r) => r.pickupPincode)).size,
      deliveryPincodes: new Set(rows.map((r) => r.deliveryPincode)).size,
      truncated: Math.max(0, rows.length - ZONE_ROW_CAP),
    },
  };
}

/** Current map back out as CSV, so download → edit → upload is lossless. */
export function zoneRowsToCsv(rows) {
  return [
    ZONE_CSV_COLUMNS.join(','),
    ...(rows || []).map((r) => [r.pickupPincode, r.deliveryPincode, r.zone].join(',')),
  ].join('\n');
}
